/**
 * Anonymous (unauthenticated) locale handling for the public marketing pages.
 *
 * The existing `useLocale` hook is auth-bound (reads/writes the backend user
 * preference). Marketing visitors have no backend preference, so this hook
 * detects the browser language and persists the choice to localStorage.
 * All `window`/`navigator`/`localStorage` access is client-only (prerender-safe).
 */
import { useCallback, useState } from 'react';
import {
  changeLanguage,
  getCurrentLanguage,
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from './i18nCore';

const STORAGE_KEY = 'streamko.lang';

function isSupported(lang: string): lang is SupportedLanguage {
  return lang in SUPPORTED_LANGUAGES;
}

/** Initial language: stored choice → first supported browser language → English. */
export function detectInitialLanguage(): SupportedLanguage {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && isSupported(stored)) return stored;
  } catch {
    /* ignore storage errors */
  }
  const navLangs =
    typeof navigator !== 'undefined' && navigator.languages ? navigator.languages : [];
  for (const tag of navLangs) {
    const base = tag.split('-')[0];
    if (isSupported(base)) return base;
  }
  return DEFAULT_LANGUAGE;
}

/** Persist a language choice (used by detection bootstrap and the switcher). */
export function persistLanguage(lang: SupportedLanguage): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* ignore storage errors */
  }
}

export interface UsePublicLocaleReturn {
  locale: SupportedLanguage;
  setLocale: (lang: SupportedLanguage) => Promise<void>;
}

export function usePublicLocale(): UsePublicLocaleReturn {
  const [locale, setLocaleState] = useState<SupportedLanguage>(getCurrentLanguage());

  const setLocale = useCallback(async (lang: SupportedLanguage) => {
    await changeLanguage(lang);
    setLocaleState(lang);
    persistLanguage(lang);
  }, []);

  return { locale, setLocale };
}
